import React, {useEffect, useMemo, createContext} from 'react';
import infulencerActions from '../actions/influencer.actions';
import getInfluencerSchema, {FormsySchema, SignupSchemaKeys} from '../data/formsy';
import userActions from '../actions/user.actions';
import eulaActions from '../actions/eula.actions';
import pageActions from '../actions/page.actions';
import {connect} from 'react-redux';
import {getLocale} from 'epic-common/src/locale';
import {getMessage} from 'epic-diesel-common';

import {
    selectPages,
    selectUser,
    selectEula,
    selectEulasLoading,
    selectInfluencer,
    selectMessages,
    selectConfig
} from '../selectors';

const mapStateToProps = state => ({
    pageState: selectPages(state),
    userState: selectUser(state),
    influencerState: selectInfluencer(state),
    messages: selectMessages(state),
    configState: selectConfig(state),
    eulasState: selectEula(state),
    isEulasLoading: selectEulasLoading(state)
});

const mapDispatchToProps = dispatch => ({
    createInfluencerProfile: model => {
        dispatch(infulencerActions.createInfluencerProfile(model));
    },
    reactivateInfluencerProfile: model => {
        dispatch(infulencerActions.reactivateInfluencerProfile(model));
    },
    resendEmailVerification: () => {
        dispatch(infulencerActions.resendEmailVerification());
    },
    checkSignupRequirementsMet: () => {
        dispatch(userActions.checkSignupRequirementsMet());
    },
    getSocialChannels: () => {
        dispatch(infulencerActions.getSocialChannels());
    },
    getAffiliateAgreement: () => {
        dispatch(eulaActions.getAffiliateAgreement());
    },
    getConductAgreement: () => {
        dispatch(eulaActions.getConductAgreement());
    },
    disconnectSocialChannel: (channel, severConnection) => {
        dispatch(
            infulencerActions.disconnectSocialChannel({
                channel,
                severConnection
            })
        );
    },
    getPage: params => {
        dispatch(pageActions.getPage(params));
    },
    resetDisconnectedPlatform: platform => {
        dispatch(infulencerActions.resetDisconnectedPlatform({platform}));
    }
});

export type WithApplicationViewProps = ReturnType<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps>;

export interface ApplicationContextValue {
    schema: FormsySchema<SignupSchemaKeys>;
}

export const ApplicationContext = createContext<ApplicationContextValue>({schema: null});

function WithApplicationView<TProps>(
    Component: React.ComponentType<TProps & WithApplicationViewProps>
) {
    const ApplicationView: React.FC<WithApplicationViewProps> = props => {
        const schema = useMemo(
            () => getInfluencerSchema(getMessage, props.messages).signupSchema(),
            []
        );

        useEffect(() => {
            const {accountInfo} = props.userState;
            const {isLoggedIn} = accountInfo || {};

            if (isLoggedIn) {
                props.getAffiliateAgreement();
                props.getConductAgreement();
                props.getPage({locale: getLocale()});
            }
        }, []);

        return (
            <ApplicationContext.Provider value={{schema}}>
                <Component {...(props as TProps & WithApplicationViewProps)} />
            </ApplicationContext.Provider>
        );
    };

    // @ts-ignore
    WithApplicationView.displayName = `WithApplicationView(${Component.displayName ||
        Component.name ||
        'Component'})`;

    return connect(
        mapStateToProps,
        mapDispatchToProps
    )(ApplicationView);
}

export default WithApplicationView;
